<template>
    <div>
        <div>{{ $t('messages.select') }}:</div>
        <div class="flex -space-x-1 flex-wrap relative z-0">
            <label v-for="teacher in filteredTeachers" :key="teacher.id">
                <input type="checkbox" v-model="teacher.is_selected" class="hidden" />

                <img
                    :title="teacher.full_name"
                    :src="teacher.image_path"
                    class="relative z-30 inline-block h-8 w-8 rounded-full ring-2 ring-white"
                    :class="{ 'opacity-50': !teacher.is_selected }"
                    alt=""
                />
            </label>
        </div>
    </div>
    <div class="mt-3">
        <NetworkGraph :edges="info.edges" :nodes="info.nodes" :options="options" />
    </div>
    <div>
        <div>{{ $t('messages.select') }}:</div>
        <div class="flex -space-x-1 flex-wrap relative z-0">
            <label v-for="student in filteredStudents" :key="student.id">
                <input type="checkbox" v-model="student.is_selected" class="hidden" />

                <img
                    :title="student.full_name"
                    :src="student.image_path"
                    class="relative z-30 inline-block h-8 w-8 rounded-lg ring-2 ring-white"
                    :class="[
                        student.is_negative ? 'bg-red-500' : 'bg-indigo-500',
                        { 'opacity-50': !student.is_selected },
                    ]"
                    alt=""
                />
            </label>
        </div>
    </div>

    <PageSectionCollapsible class="mt-3">
        <template v-slot:title>
            {{ $t('messages.summary') }}
        </template>
        <div>
            <div class="flex flex-col mt-5">
                <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                    <div
                        class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                    >
                        <table class="divide-y divide-gray-200">
                            <thead>
                                <tr>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        -
                                    </th>
                                    <th
                                        v-for="teacher in table.header"
                                        :key="teacher.id"
                                        class="max-w-xs px-6 py-1 bg-gray-50 text-center text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        <img
                                            :title="teacher.full_name"
                                            :src="teacher.avatar_or_default"
                                            class="relative z-30 inline-block h-8 w-8 rounded-lg ring-2 ring-white"
                                            alt=""
                                        />
                                    </th>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.total') }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                    v-for="(student, index) in selectedStudentRows"
                                    :key="student.id"
                                >
                                    <td
                                        class="max-w-sm truncate px-6 py-1 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                    >
                                        <img
                                            :title="student.full_name"
                                            :src="student.avatar_or_default"
                                            class="relative z-30 inline-block h-8 w-8 rounded-lg ring-2 ring-white"
                                            alt=""
                                        />
                                    </td>

                                    <td
                                        v-for="(column, columnIndex) in student.columns"
                                        :key="columnIndex"
                                        class="max-w-sm truncate px-6 py-1 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                    >
                                        <span
                                            :class="{
                                                'text-gray-300': column === 0,
                                            }"
                                        >
                                            {{ column }}
                                        </span>
                                    </td>
                                    <td
                                        class="max-w-sm truncate px-6 py-1 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                    >
                                        <span
                                            :class="{
                                                'text-gray-300': student.total === 0,
                                            }"
                                        >
                                            {{ student.total }}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </PageSectionCollapsible>
</template>

<script>
export default {
    name: 'NetworkGraphRewardTeachersStudents',
};
</script>
<script setup>
import { computed, ref } from 'vue';
import NetworkGraph from '@/components/pageSpecific/NetworkGraph.vue';
import PageSectionCollapsible from '../../Shared/PageSectionCollapsible.vue';

const props = defineProps({
    teachers: {
        required: true,
        type: Array,
    },
    students: {
        required: true,
        type: Array,
    },
    table: {
        required: true,
        type: Object,
    },
    isNegative: {
        type: Boolean,
        default: false,
    },
});

const filteredTeachers = ref(props.teachers);
const filteredStudents = ref(props.students);
const filteredStudentIds = computed(() => filteredStudents.value.filter((s) => s.is_selected).map((s) => s.id));

const selectedStudentRows = computed(() => {
    return props.table.rows.filter((s) => filteredStudentIds.value.includes(s.id));
});

const info = computed(() => {
    const nodes = [];
    const edges = [];
    filteredTeachers.value
        .filter((t) => t.is_selected)
        .forEach((teacher) => {
            nodes.push({
                id: `t_${teacher.id}`,
                shape: 'circularImage',
                image: teacher.image_path,
                group: 'teacher',
                title: teacher.full_name,
                label: teacher.name_or_nickname,
            });

            teacher.students.forEach((student) => {
                let color = props.isNegative ? '#ef4444' : '#6366f1';
                edges.push({
                    from: `t_${teacher.id}`,
                    to: `r_${student.student_id}`,
                    label: `${student.qty}`,
                    title: student.qty,
                    value: student.qty,
                    color: color,
                });
            });
        });
    filteredStudents.value
        .filter((r) => r.is_selected)
        .forEach((student) => {
            nodes.push({
                id: `r_${student.id}`,
                shape: 'image',
                image: student.image_path,
                group: 'students',
                title: student.full_name,
                label: student.name_or_nickname,
            });
        });

    return {
        nodes,
        edges,
    };
});

const options = {
    nodes: {
        borderWidth: 4,
        size: 30,
        color: {
            border: '#222222',
            background: '#666666',
        },
    },
    edges: {
        color: {
            color: 'green',
            highlight: '#2ed300',
            hover: '#848484',
            inherit: 'from',
            opacity: 0.4,
        },
        arrows: {
            to: {
                enabled: true,
                scaleFactor: 0.5,
                type: 'arrow',
            },
        },
        scaling: {
            customScalingFunction: function (min, max, total, value) {
                return value / total;
            },
            min: 1,
            max: 25,
        },
        selectionWidth: function (width) {
            return width * 2;
        },
    },
};
</script>
